import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Switch } from '@/shared/components/ui/switch';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Bell,
  Mail,
  MessageSquare,
  Phone,
  Send,
  Edit,
  Trash2,
  Play,
  Pause,
  Settings,
  Zap,
  ArrowRight,
  Loader2 } from
'lucide-react';

const API_URL = API_BASE;

const moduleColors = {
  ezparcel: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-600', border: 'border-red-500' },
  wms: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-600', border: 'border-blue-500' },
  finance: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-600', border: 'border-emerald-500' },
  procurement: { bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-600', border: 'border-indigo-500' },
  crm: { bg: 'bg-teal-100 dark:bg-teal-900/30', text: 'text-teal-600', border: 'border-teal-500' },
  system: { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-600', border: 'border-gray-500' }
};

const channelIcons = {
  email: Mail,
  whatsapp: MessageSquare,
  sms: Phone,
  telegram: Send
};

export const NotificationRules = () => {const { t } = useTranslation("comms");
  const [searchTerm, setSearchTerm] = useState('');
  const [rules, setRules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [rulesRes, templatesRes] = await Promise.all([
        fetch(`${API_URL}/api/communications/notification-rules`),
        fetch(`${API_URL}/api/communications/message-templates`)]
        );

        if (rulesRes.ok) {
          const rulesData = await rulesRes.json();
          // Transform API data to component format
          const transformedRules = rulesData.map((rule) => ({
            id: `rule_${rule.id}`,
            name: rule.name,
            module: rule.module,
            event: rule.event,
            enabled: rule.enabled,
            channels: rule.channels || [],
            template: rule.name,
            recipients: rule.conditions?.recipients || 'all',
            conditions: Object.entries(rule.conditions || {}).map(([field, value]) => ({
              field,
              operator: typeof value === 'string' && value.startsWith('>') ? 'gte' : 'eq',
              value: value.toString().replace(/[<>]/g, '')
            })).filter((c) => c.field !== 'recipients'),
            priority: rule.priority || 'medium',
            schedule: null
          }));
          setRules(transformedRules);
        }

        if (templatesRes.ok) {
          const templatesData = await templatesRes.json();
          setTemplates(templatesData);
        }
      } catch (err) {
        console.error('Failed to fetch notification rules:', err);
        toast.error('Failed to load notification rules');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleToggleRule = async (ruleId, enabled) => {
    const numericId = parseInt(ruleId.replace('rule_', ''));
    try {
      const response = await fetch(`${API_URL}/api/communications/notification-rules/${numericId}?enabled=${enabled}`, {
        method: 'PUT'
      });
      if (response.ok) {
        setRules((prev) => prev.map((r) => r.id === ruleId ? { ...r, enabled } : r));
        toast.success(`Rule ${enabled ? 'enabled' : 'disabled'}`);
      }
    } catch (err) {
      toast.error('Failed to update rule');
    }
  };

  const filteredRules = rules.filter((rule) =>
  rule.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
  rule.module.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getTemplateName = (templateId) => {
    const template = templates.find((t) => t.id === templateId || t.name === templateId);
    return template?.name || templateId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-violet-600" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="communications-rules">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("notificationRules", "Notification Rules")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("configureAutomatedNotifications", "Configure automated notifications for system events")}

          </p>
        </div>
        <Button className="gap-2 bg-violet-600 hover:bg-violet-700">
          <Plus className="h-4 w-4" />{t("createRule", "Create Rule")}

        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <Card className="card-elevated border-l-4 border-l-violet-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{t("totalRules", "Total Rules")}</p>
            <p className="text-2xl font-bold">{rules.length}</p>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{t("active", "Active")}</p>
            <p className="text-2xl font-bold">{rules.filter((r) => r.enabled).length}</p>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{t("modulesCovered", "Modules Covered")}</p>
            <p className="text-2xl font-bold">{new Set(rules.map((r) => r.module)).size}</p>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{t("scheduled", "Scheduled")}</p>
            <p className="text-2xl font-bold">{rules.filter((r) => r.schedule).length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="card-elevated">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search rules by name or module..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10" />
            
          </div>
        </CardContent>
      </Card>

      {/* Rules List */}
      <div className="space-y-4">
        {filteredRules.map((rule) => {
          const colors = moduleColors[rule.module];
          return (
            <Card key={rule.id} className={`card-elevated border-l-4 ${colors.border} hover:shadow-lg transition-all`}>
              <CardContent className="p-5">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`p-2 rounded-lg ${colors.bg}`}>
                        <Zap className={`h-5 w-5 ${colors.text}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold">{rule.name}</h3>
                        <p className="text-sm text-muted-foreground">{rule.id}</p>
                      </div>
                    </div>

                    {/* Event Flow */}
                    <div className="flex items-center gap-2 flex-wrap mt-3">
                      <Badge className={`${colors.bg} ${colors.text}`}>
                        {rule.module}
                      </Badge>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      <Badge variant="outline" className="font-mono text-xs">
                        {rule.event}
                      </Badge>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      <div className="flex gap-1">
                        {rule.channels.map((channel) => {
                          const Icon = channelIcons[channel];
                          return (
                            <div key={channel} className={`p-1.5 rounded ${
                            channel === 'email' ? 'bg-violet-100 dark:bg-violet-900/30' :
                            channel === 'whatsapp' ? 'bg-emerald-100 dark:bg-emerald-900/30' :
                            channel === 'sms' ? 'bg-orange-100 dark:bg-orange-900/30' :
                            'bg-blue-100 dark:bg-blue-900/30'}`
                            }>
                              <Icon className={`h-3.5 w-3.5 ${
                              channel === 'email' ? 'text-violet-600' :
                              channel === 'whatsapp' ? 'text-emerald-600' :
                              channel === 'sms' ? 'text-orange-600' :
                              'text-blue-600'}`
                              } />
                            </div>);

                        })}
                      </div>
                    </div>

                    {/* Template & Recipients */}
                    <div className="flex items-center gap-4 mt-3 text-sm">
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <Bell className="h-4 w-4" />
                        <span>{t("template", "Template:")}</span>
                        <span className="font-medium text-foreground">{getTemplateName(rule.template)}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <span>{t("recipients", "Recipients:")}</span>
                        <Badge variant="secondary" className="text-xs">{rule.recipients}</Badge>
                      </div>
                      {rule.schedule &&
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                          <span>{t("schedule", "Schedule:")}</span>
                          <Badge variant="secondary" className="text-xs">{rule.schedule}</Badge>
                        </div>
                      }
                    </div>

                    {/* Conditions */}
                    {rule.conditions.length > 0 &&
                    <div className="mt-2">
                        <span className="text-xs text-muted-foreground">{t("conditions", "Conditions:")}</span>
                        {rule.conditions.map((cond, idx) =>
                      <Badge key={idx} variant="outline" className="text-xs font-mono ml-1">
                            {cond.field} {cond.operator} {cond.value}
                          </Badge>
                      )}
                      </div>
                    }
                  </div>

                  <div className="flex items-center gap-3">
                    <Switch
                      checked={rule.enabled}
                      onCheckedChange={(checked) => handleToggleRule(rule.id, checked)} />
                    
                    <Badge className={rule.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}>
                      {rule.enabled ? <Play className="h-3 w-3 mr-1" /> : <Pause className="h-3 w-3 mr-1" />}
                      {rule.enabled ? 'Active' : 'Paused'}
                    </Badge>
                    <Button variant="outline" size="icon" className="h-8 w-8">
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>);

        })}
      </div>

      {filteredRules.length === 0 &&
      <Card className="card-elevated">
          <CardContent className="py-12 text-center">
            <Bell className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">{t("noRulesFound", "No rules found")}</h3>
            <p className="text-muted-foreground">{t("tryAdjustingYour", "Try adjusting your search criteria")}</p>
          </CardContent>
        </Card>
      }
    </div>);

};